import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import jsPDF from 'jspdf';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import Spinner from '../components/Spinner';

declare global {
  interface Window {
    PaystackPop?: {
      setup: (config: any) => {
        openIframe: () => void;
      };
    };
  }
}

const categories = [
  { value: 'school_fees', label: 'School Fees' },
  { value: 'extracurricular', label: 'Extracurricular Activities' },
  { value: 'other', label: 'Other' }
];

const ParentPayments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [category, setCategory] = useState('school_fees');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [paystackReady, setPaystackReady] = useState(false);

  useEffect(() => {
    const scriptId = 'parent-paystack-inline-script';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (!existing) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.async = true;
      script.onload = () => setPaystackReady(true);
      script.onerror = () => {
        console.error('Failed to load Paystack script for parent payments');
        setPaystackReady(false);
      };
      document.body.appendChild(script);
    } else if (window.PaystackPop) {
      setPaystackReady(true);
    }
  }, []);

  const loadPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/parent-payments');
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load payments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, []);

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    const parsedAmount = Number(amount);
    if (!category) {
      setError('Please select a payment category.');
      return;
    }
    if (!parsedAmount || parsedAmount <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '';
    const parentEmail = user?.parentEmail || user?.email || '';

    setSubmitting(true);
    try {
      if (publicKey && window.PaystackPop && parentEmail) {
        const reference = `parent_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        let paymentHandled = false;

        const handlePaystackSuccess = function (response: any) {
          paymentHandled = true;
          api.post('/parent-payments', {
            category,
            amount: parsedAmount,
            description: description.trim() || undefined,
            reference: response?.reference || reference,
            paymentProvider: 'paystack'
          })
            .then((response) => {
              const payment = response.data.payment || response.data;
              setPayments(prev => [payment, ...prev]);
              setSuccess('Payment submitted successfully.');
              setAmount('');
              setDescription('');
              setSubmitting(false);
            })
            .catch((err: any) => {
              console.error('Parent payment verification failed:', err);
              setError(err?.response?.data?.message || 'Unable to verify the Paystack payment.');
              setSubmitting(false);
            });
        };

        const handlePaystackClose = function () {
          if (paymentHandled) return;
          setSubmitting(false);
          setError('Payment was cancelled before completion.');
        };

        const paystackConfig: any = {
          key: publicKey,
          email: parentEmail,
          amount: Math.round(parsedAmount * 100),
          currency: 'NGN',
          ref: reference,
          callback: handlePaystackSuccess,
          onClose: handlePaystackClose,
        };

        const paystack = window.PaystackPop.setup(paystackConfig);
        paystack.openIframe();
        return;
      }

      const response = await api.post('/parent-payments', {
        category,
        amount: parsedAmount,
        description: description.trim() || undefined
      });
      const payment = response.data.payment || response.data;
      setPayments(prev => [payment, ...prev]);
      setSuccess('Payment submitted successfully.');
      setAmount('');
      setDescription('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to submit payment.');
    } finally {
      setSubmitting(false);
    }
  };

  const isValidReceipt = (payment: any) => Boolean(
    payment &&
    typeof payment === 'object' &&
    (payment._id || payment.transactionId || payment.createdAt || payment.date || payment.amount !== undefined || payment.category)
  );

  const openReceipt = (payment: any) => {
    if (!isValidReceipt(payment)) return;

    const paymentId = payment._id || payment.transactionId || payment.createdAt || payment.date;
    if (!paymentId) return;

    navigate(`/parent/payments/receipt/${encodeURIComponent(String(paymentId))}`, { state: { payment } });
  };

  const getReceiptData = (payment?: any) => {
    return isValidReceipt(payment) ? payment : null;
  };

  const downloadReceipt = (payment?: any) => {
    const selectedReceipt = getReceiptData(payment);
    if (!selectedReceipt) return;

    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const amountValue = Number(selectedReceipt.amount || 0);
    const categoryLabel = categories.find((opt) => opt.value === selectedReceipt.category)?.label || selectedReceipt.category || 'Other';
    const descriptionText = selectedReceipt.description || 'No description provided';
    const transactionId = selectedReceipt.transactionId || 'N/A';
    const paymentDate = new Date(selectedReceipt.createdAt || selectedReceipt.date).toLocaleString();
    const statusText = String(selectedReceipt.status || 'completed').toUpperCase();

    doc.setFillColor(31, 62, 133);
    doc.rect(0, 0, pageWidth, 36, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    doc.text('A3 Tech', 14, 23);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Parent Payment Receipt', pageWidth - 48, 23, { align: 'right' });

    doc.setTextColor(40, 40, 40);
    doc.setDrawColor(196, 196, 196);
    doc.setLineWidth(0.5);
    doc.line(14, 48, pageWidth - 14, 48);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Payment Confirmation', 14, 62);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    const rows = [
      ['Receipt No.', transactionId],
      ['Student / Guardian', user?.name || user?.parentName || 'N/A'],
      ['Email', user?.parentEmail || user?.email || 'N/A'],
      ['Category', categoryLabel],
      ['Status', statusText],
      ['Date', paymentDate],
      ['Amount', `₦${amountValue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
      ['Description', descriptionText]
    ];

    let yPos = 78;
    rows.forEach(([label, value], index) => {
      const labelX = 18;
      const valueX = 78;

      doc.setFillColor(index % 2 === 0 ? 248 : 255, 249, 250);
      doc.rect(14, yPos - 5, pageWidth - 28, 14, 'F');
      doc.setDrawColor(228, 232, 240);
      doc.rect(14, yPos - 5, pageWidth - 28, 14, 'S');

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(52, 58, 64);
      doc.text(String(label), labelX, yPos + 2);

      doc.setFont('helvetica', 'normal');
      doc.text(String(value), valueX, yPos + 2);
      yPos += 16;
    });

    doc.setDrawColor(218, 221, 226);
    doc.line(14, yPos + 8, pageWidth - 14, yPos + 8);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(31, 62, 133);
    doc.text('Total Paid', 14, yPos + 24);
    doc.text(`₦${amountValue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - 40, yPos + 24, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110, 116, 122);
    doc.text('Thank you for paying with A3 Tech. This receipt confirms your successful payment.', 14, pageHeight - 24);
    doc.text('Generated on ' + new Date().toLocaleString(), pageWidth - 50, pageHeight - 24, { align: 'right' });

    doc.save(`parent-payment-receipt-${transactionId || 'receipt'}.pdf`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        School Fees & Activity Payments
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Make a Payment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Use this form to pay school fees, extracurricular activity charges, or other school-related payments.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="payment-category-label">Payment category</InputLabel>
                  <Select
                    labelId="payment-category-label"
                    label="Payment category"
                    value={category}
                    onChange={(event) => setCategory(event.target.value as string)}
                  >
                    {categories.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Amount"
                  type="number"
                  fullWidth
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description (optional)"
                  multiline
                  minRows={3}
                  fullWidth
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </Grid>
            </Grid>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
              <Button variant="contained" onClick={handleSubmit} disabled={submitting || !paystackReady}>
                {submitting ? <CircularProgress size={20} /> : 'Pay now'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/parent/dashboard')}>
                Back to dashboard
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Payment history
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <Spinner />
              </Box>
            ) : payments.length === 0 ? (
              <Typography color="text.secondary">No payments have been recorded yet.</Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Receipt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment._id || payment.transactionId || payment.date}>
                        <TableCell>{new Date(payment.createdAt || payment.date).toLocaleDateString()}</TableCell>
                        <TableCell>{categories.find((opt) => opt.value === payment.category)?.label || payment.category || 'Other'}</TableCell>
                        <TableCell>{payment.description || '—'}</TableCell>
                        <TableCell>₦{payment.amount?.toFixed ? payment.amount.toFixed(2) : payment.amount}</TableCell>
                        <TableCell>{payment.status || 'pending'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button size="small" variant="outlined" onClick={() => openReceipt(payment)}>
                              View
                            </Button>
                            <Button size="small" variant="contained" onClick={() => downloadReceipt(payment)}>
                              Download PDF
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card elevation={1} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Student information
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Student: {user?.registrationId || user?.name || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Class: {user?.className || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Guardian: {user?.parentName || user?.name || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Phone: {user?.parentPhone || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Email: {user?.parentEmail || user?.email || 'N/A'}
              </Typography>
            </CardContent>
          </Card>

          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment tips
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Use the correct amount from your school invoice or activity notice.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                After payment, you can track the record in this payment history section.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ParentPayments;
